import { Info, Trash2, TrendingUp } from "lucide-react";
import moment from "moment-timezone";
import Image from "next/image";
import React, { useRef, useEffect } from "react";
import { FlashingValue } from "src/common/FlashingValue";
import BriefCaseFilledIcon from "src/assets/images/Briefcase-Fiilled.svg";
import Briefcase from "src/assets/images/Briefcase.svg";
import hotFlame from "src/assets/images/fire.png";
import OrderBlock from "src/assets/images/OrderBlock.svg";
import PointDown from "src/assets/images/PointDown.svg";
import PointUp from "src/assets/images/PointUp.svg";
import SwapIcon from "src/assets/images/swap.png";
import WatchlistFilledIcon from "src/assets/images/WatchList-Filled.svg";
import WatchlistIcon from "src/assets/images/Watchlist.svg";
import SnowflakeIcon from "src/common/SnowflakeIcon";
import TableSkeleton from "src/components/skeleton/TableSkeleton";
import { formatToCr } from "src/helpers/api/sharedFunction";
import {
  formatCurrencyWithLocalString,
  formatPercentageWithLocalString,
} from "src/helpers/numberFormatter";
import { StockTableData } from "src/helpers/table-objects";
import { SortConfig } from "src/helpers/api/sortconfig";

export interface CommonStockTableProps {
  records: StockTableData[];
  isLoading?: boolean;
  sortConfig?: SortConfig | null;
  onSort?: (key: string) => void;
  getSortIcon?: (config: SortConfig | null, key: string) => React.ReactNode;
  onRowClick?: (record: StockTableData) => void;
  selectedCompanySymbol?: string;
  highlightSymbol?: string;
  onHighlightEnd?: (symbol: string) => void;
  darkMode?: boolean;
  isDashboard?: boolean;
  selectedDate?: string;
  onAddPortfolio?: (record: StockTableData) => void;
  onAddWatchlist?: (record: StockTableData) => void;
  onRemoveWatchlist?: (record: StockTableData) => void;
  onOpenInsights?: (symbol: string, tab?: string) => void;
  onOpenBusinessInsight?: (symbol: string) => void;
  onOpenForensicInsight?: (symbol: string) => void;
  onOpenInsiderTrading?: (symbol: string) => void;
  onOpenChart?: (symbol: string, date: string) => void;
  onOpenOrderBlock?: (record: StockTableData) => void;
  isPortfolioLimitReached?: (record: StockTableData) => boolean;
  isWatchlistLimitReached?: (record: StockTableData) => boolean;
  onLimitReached?: (type: "portfolio" | "watchlist") => void;
  containerClassName?: string;
  emptyState?: React.ReactNode;
  skeletonRows?: number;
}

export const CommonStockTable: React.FC<CommonStockTableProps> = ({
  records,
  isLoading,
  sortConfig,
  onSort,
  getSortIcon,
  onRowClick,
  selectedCompanySymbol = "",
  highlightSymbol = "",
  onHighlightEnd,
  darkMode = false,
  onAddPortfolio,
  onAddWatchlist,
  onRemoveWatchlist,
  onOpenInsights,
  onOpenBusinessInsight,
  onOpenForensicInsight,
  onOpenInsiderTrading,
  onOpenChart,
  onOpenOrderBlock,
  isPortfolioLimitReached,
  isWatchlistLimitReached,
  onLimitReached,
  containerClassName = "",
  emptyState,
  skeletonRows,
  selectedDate,
}) => {
  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});
  const onHighlightEndRef = useRef(onHighlightEnd);
  useEffect(() => {
    onHighlightEndRef.current = onHighlightEnd;
  }, [onHighlightEnd]);

  useEffect(() => {
    if (highlightSymbol && rowRefs.current[highlightSymbol]) {
      const rowEl = rowRefs.current[highlightSymbol];
      if (rowEl) {
        setTimeout(() => {
          rowEl.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }, 100);

        if (onHighlightEndRef.current) {
          const timer = setTimeout(() => {
            onHighlightEndRef.current?.(highlightSymbol);
          }, 2000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [highlightSymbol]);

  const handleSortClick = (key: string) => {
    if (onSort) onSort(key);
  };

  const renderSortIcon = (key: string) => {
    if (getSortIcon) return getSortIcon(sortConfig || null, key);
    return null;
  };

  const getHotMonths = (referenceDate: moment.Moment) => {
    const monthsToCheck: string[] = [];
    for (let i = 0; i < 2; i++) {
      const monthName = referenceDate.clone().add(i, "months").format("MMM");
      monthsToCheck.push(monthName);
    }
    return monthsToCheck;
  };

  const renderScoreBar = (
    score: number | undefined | null,
    isOverall: boolean = false,
    onClick?: () => void,
    isSelected?: boolean,
  ) => {
    if (score === undefined || score === null) {
      return (
        <div
          className={`flex flex-col items-center justify-center w-[50px] mx-auto py-1 ${onClick ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
          onClick={(e) => {
            if (onClick) {
              e.stopPropagation();
              onClick();
            }
          }}
          title={onClick ? "View Details" : undefined}
        >
          <span className="text-gray-500">-</span>
        </div>
      );
    }

    let colorClass = "text-[#00a85f] dark:text-green-400";
    let glowClass = "hover:drop-shadow-[0_0_5px_rgba(0,168,95,0.8)]";
    let defaultGlowAnim = isSelected ? "animate-glow-green" : "";

    const normalizedScore = score * 10;
    let displayScore = isOverall ? `${score.toFixed(1)}/10` : score;

    if (normalizedScore < 40) {
      colorClass = "text-red-500 dark:text-red-400";
      glowClass = "hover:drop-shadow-[0_0_5px_rgba(239,68,68,0.8)]";
      defaultGlowAnim = isSelected ? "animate-glow-red" : "";
    } else if (normalizedScore < 70) {
      colorClass = "text-orange-500 dark:text-orange-400";
      glowClass = "hover:drop-shadow-[0_0_5px_rgba(249,115,22,0.8)]";
      defaultGlowAnim = isSelected ? "animate-glow-orange" : "";
    }

    // If it's overall score, just show text prominently
    if (isOverall) {
      return (
        <div className="flex flex-col items-center justify-center w-[50px] mx-auto py-1">
          <span
            className={`text-[14px] font-semibold font-figtree transition-colors ${colorClass}`}
          >
            {displayScore}
          </span>
        </div>
      );
    }

    // Semi-Circle Gauge for Financial, Fundamental, Forensic
    const radius = 16;
    const circumference = Math.PI * radius;
    const strokeDashoffset =
      circumference -
      (Math.min(100, Math.max(0, normalizedScore)) / 100) * circumference;

    return (
      <div
        className={`relative flex flex-col items-center justify-center w-[46px] h-[28px] mx-auto group ${onClick ? "cursor-pointer" : ""}`}
        onClick={(e) => {
          if (onClick) {
            e.stopPropagation();
            onClick();
          }
        }}
        title={onClick ? "View Details" : undefined}
      >
        <svg
          className={`w-full h-full transition-all duration-300 ${onClick ? glowClass : ""} ${defaultGlowAnim}`}
          viewBox="0 0 40 24"
        >
          <path
            d="M 4 20 A 16 16 0 0 1 36 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            className="text-gray-200 dark:text-[#394564]"
          />
          <path
            d="M 4 20 A 16 16 0 0 1 36 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={`${colorClass} transition-all duration-1000 ease-out`}
          />
        </svg>
        <span
          className={`absolute bottom-[-3px] left-1/2 transform -translate-x-1/2 text-[11px] font-semibold font-figtree transition-colors ${colorClass} ${onClick ? "group-hover:underline decoration-dashed underline-offset-2" : ""}`}
        >
          {displayScore}
        </span>
      </div>
    );
  };

  return (
    <div
      className={`transition-all duration-300 overflow-auto ${containerClassName}`}
    >
      {/* Global scrollbar style included at layout level usually, but we keep it modular here */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .overflow-auto {
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
          scrollbar-color: rgb(193, 193, 193) rgb(241, 241, 241);
        }
        .overflow-auto::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .overflow-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .overflow-auto::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        .overflow-auto::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        .dark .overflow-auto {
          scrollbar-color: #4a5568 #2d3748;
        }
        .dark .overflow-auto::-webkit-scrollbar-track {
          background: #2d3748;
        }
        .dark .overflow-auto::-webkit-scrollbar-thumb {
          background: #4a5568;
        }
        .dark .overflow-auto::-webkit-scrollbar-thumb:hover {
          background: #718096;
        }
        @media (prefers-color-scheme: dark) {
          .dark {
            scrollbar-color: #4a5568 #2d3748;
          }
        }
        @keyframes glowHighlightGreen {
          0% {
            filter: drop-shadow(0 0 8px rgba(0, 168, 95, 0.9));
          }
          100% {
            filter: drop-shadow(0 0 0px transparent);
          }
        }
        @keyframes glowHighlightOrange {
          0% {
            filter: drop-shadow(0 0 8px rgba(249, 115, 22, 0.9));
          }
          100% {
            filter: drop-shadow(0 0 0px transparent);
          }
        }
        @keyframes glowHighlightRed {
          0% {
            filter: drop-shadow(0 0 8px rgba(239, 68, 68, 0.9));
          }
          100% {
            filter: drop-shadow(0 0 0px transparent);
          }
        }
        .animate-glow-green {
          animation: glowHighlightGreen 3s ease-out forwards;
        }
        .animate-glow-orange {
          animation: glowHighlightOrange 3s ease-out forwards;
        }
        .animate-glow-red {
          animation: glowHighlightRed 3s ease-out forwards;
        }
      `,
        }}
      />

      <table className="w-full min-w-max">
        <thead className="sticky top-0 z-20">
          <tr className="bg-[#f4f5f8] dark:bg-[#30324e] border-b border-[#d0d4e4] dark:border-[#394564]">
            {/* Symbol & Market Cap */}
            <th className="text-left py-3 px-4 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564]">
              <div className="flex items-center gap-1">
                <span>Symbol</span>
                <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
              </div>
            </th>
            {/* Risk Profile */}
            <th
              className="text-center py-3 px-2 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("riskprofile")}
            >
              <div className="flex items-center justify-center gap-1">
                Risk {renderSortIcon("riskprofile")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* Close */}
            <th className="text-center py-3 px-2 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564]">
              Close
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* 52 Week High */}
            <th
              className="text-center py-3 px-2 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("fifty_two_week_high")}
            >
              <div className="flex items-center justify-center gap-1">
                52 week High {renderSortIcon("fifty_two_week_high")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* Probability */}
            <th
              className="text-center py-3 px-2 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("probability")}
            >
              <div className="flex items-center justify-center gap-1">
                Probability {renderSortIcon("probability")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* RSI Sub-columns */}
            <th className="text-center pt-1 mb-1 px-0 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564]">
              <div className="flex items-center justify-center">
                <div>RSI</div>
              </div>
              <div className="flex items-center justify-around min-w-[180px] py-1 border-t border-solid border-[#d0d4e4] dark:border-[#394564] shadow-inner shadow-white/50 dark:shadow-black/20">
                <div
                  className="relative group w-1/3 text-[11px] cursor-pointer flex justify-center items-center h-full"
                  onClick={() => handleSortClick("rsi_daily")}
                >
                  Daily {renderSortIcon("rsi_daily")}
                </div>
                <div
                  className="relative group w-1/3 text-[11px] cursor-pointer flex justify-center items-center h-full"
                  onClick={() => handleSortClick("rsi_weekly")}
                >
                  Weekly {renderSortIcon("rsi_weekly")}
                </div>
                <div
                  className="relative group w-1/3 text-[11px] cursor-pointer flex justify-center items-center h-full"
                  onClick={() => handleSortClick("rsi_monthly")}
                >
                  Monthly {renderSortIcon("rsi_monthly")}
                </div>
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* Peak Return */}
            <th
              className="text-center py-3 px-2 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("high_return_perc_num")}
            >
              <div className="flex items-center justify-center gap-1">
                Peak Return {renderSortIcon("high_return_perc_num")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* Current Return */}
            <th
              className="text-center py-3 px-2 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("current_percent")}
            >
              <div className="flex items-center justify-center gap-1">
                Current Return {renderSortIcon("current_percent")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* Score Columns */}
            <th
              className="text-center py-3 px-2 text-[11px] lg:text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("financial_score")}
            >
              <div className="flex items-center justify-center gap-1">
                Financial {renderSortIcon("financial_score")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>
            <th
              className="text-center py-3 px-2 text-[11px] lg:text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("fundamental_score")}
            >
              <div className="flex items-center justify-center gap-1">
                Research {renderSortIcon("fundamental_score")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>
            <th
              className="text-center py-3 px-2 text-[11px] lg:text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564] cursor-pointer group"
              onClick={() => handleSortClick("forensic_score")}
            >
              <div className="flex items-center justify-center gap-1">
                Forensic {renderSortIcon("forensic_score")}
              </div>
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>
            <th className="text-center py-3 px-2 text-[11px] lg:text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree relative table-border-right after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[0.5px] after:bg-[#d0d4e4] dark:after:bg-[#394564]">
              Overall
              <span className="absolute right-0 top-0 h-full w-[1px] bg-[#d0d4e4] dark:bg-[#394564]"></span>
            </th>

            {/* Action */}
            <th className="text-center py-3 px-3 text-[13px] font-medium normal-case text-[#3D3D3D] dark:text-white tracking-wider font-figtree after:content-[''] after:absolute after:left-0 after:bottom-0 after:w-full after:h-[1px] after:bg-[#d0d4e4] dark:after:bg-[#394564]">
              Action
            </th>
          </tr>
        </thead>

        <tbody>
          {isLoading ? (
            <TableSkeleton rows={skeletonRows} />
          ) : records.length === 0 ? (
            <tr>
              <td
                colSpan={12}
                className="py-6 px-6 text-center text-[15px] font-normal text-gray-700 dark:text-[#ffffff] tracking-wide font-figtree"
              >
                {emptyState || "No records found"}
              </td>
            </tr>
          ) : (
            records.map((record, index) => {
              const isSelected =
                selectedCompanySymbol.replace(".NS", "") === record.symbol;
              const isHighlighted = highlightSymbol === record.symbol;

              const fiftyTwoWeekHighColor =
                record.fifty_two_week_high_perc === 0
                  ? "text-gray-900 dark:text-white"
                  : record.fifty_two_week_high_perc < 0
                    ? "text-red-500 dark:text-red-400"
                    : "text-[#00a85f] dark:text-green-400";
              const peakReturnColor =
                Number(record.high_return_perc) === 0
                  ? "text-gray-900 dark:text-white"
                  : Number(record.high_return_perc) < 0
                    ? "text-red-500 dark:text-red-400"
                    : "text-[#00a85f] dark:text-green-400";
              const currentReturnColor =
                record.ltp && record.close && record.ltp - record.close === 0
                  ? "text-gray-900 dark:text-white"
                  : record.ltp && record.close && record.ltp - record.close < 0
                    ? "text-red-500 dark:text-red-400"
                    : "text-[#00a85f] dark:text-green-400";

              return (
                <tr
                  key={`${record.symbol}-${index}`}
                  className={`
                    border-b border-[#d0d4e4] dark:border-[#394564] duration-200 
                    ${onRowClick ? "cursor-pointer dark:hover:bg-[#394564]/30 hover:bg-[#f4f5f8]/50" : ""}
                    ${isSelected ? "border-l-[2px] border-l-[#00a85f] dark:border-l-[#00a85f]" : ""}
                  `}
                  style={{
                    backgroundColor: isSelected
                      ? isHighlighted
                        ? "transparent"
                        : darkMode
                          ? "rgba(0, 34, 85, 0.5)"
                          : "#edf1fc"
                      : "transparent",
                    animation: isHighlighted
                      ? darkMode
                        ? "blinkDark 2s ease"
                        : "blinkLight 2s ease"
                      : "none",
                  }}
                  ref={(el) => {
                    rowRefs.current[record.symbol] = el;
                  }}
                  onClick={() => onRowClick && onRowClick(record)}
                  onAnimationEnd={() => {
                    if (isHighlighted && onHighlightEnd) {
                      onHighlightEnd(record.symbol);
                    }
                  }}
                >
                  {/* Symbol & Market Cap */}
                  <td className="py-2 px-4 font-figtree">
                    <div className="flex items-center justify-between w-full group">
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-center space-x-1 relative">
                          {record.ltp === record.close ? (
                            <div className="w-3 h-3" /> // spacer
                          ) : record.ltp > record.close ? (
                            <Image
                              src={PointUp}
                              alt="Increase"
                              className="w-3 h-3 transition-transform group-hover:scale-110"
                              style={{
                                filter:
                                  "brightness(0) saturate(100%) invert(31%) sepia(98%) saturate(2089%) hue-rotate(131deg) brightness(97%) contrast(100%)",
                              }}
                            />
                          ) : (
                            <Image
                              src={PointDown}
                              alt="Decrease"
                              className="w-3 h-3 transition-transform group-hover:scale-110"
                              style={{
                                filter:
                                  "brightness(0) saturate(100%) invert(27%) sepia(100%) saturate(7498%) hue-rotate(357deg) brightness(99%) contrast(116%)",
                              }}
                            />
                          )}

                          <span className="font-medium text-black dark:text-white text-[13px] tracking-wide transition-colors">
                            {record.symbol}
                          </span>

                          {/* Extra Badges / Actions (Dashboard Only usually) */}
                          <div className="flex items-center gap-1.5 ml-2">
                            {record?.hot_flame && (
                              <div className="relative group/flame inline-flex shrink-0 items-center">
                                <Image
                                  src={hotFlame}
                                  alt="HotStock"
                                  className="w-3.5 h-3.5 transition-transform dark:hover:scale-110"
                                />
                                <div className="absolute z-10 hidden w-80 top-[-60px] translate-y-0 sm:w-max sm:top-1/2 sm:-translate-y-1/2 group-hover/flame:block bg-[#30324e] text-white text-[13px] p-2 rounded-md shadow-lg border border-[#394564] left-full ml-0 sm:ml-2">
                                  This stock is identified as a high performer
                                  based on its 2-months performance trend. (
                                  {getHotMonths(moment(selectedDate)).join(
                                    ", ",
                                  )}
                                  )
                                  <div className="absolute hidden sm:block right-full top-1/2 transform -translate-y-1/2 w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-r-4 border-r-[#30324e]"></div>
                                </div>
                              </div>
                            )}
                            {record?.snow_flake && <SnowflakeIcon />}
                            {record.is_retest &&
                              record.obdate &&
                              record.retest_date &&
                              onOpenOrderBlock && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenOrderBlock(record);
                                  }}
                                  title="OB Info"
                                  className="inline-flex shrink-0 items-center justify-center hover:opacity-80 transition-opacity"
                                >
                                  <Image
                                    src={OrderBlock}
                                    alt="OrderBlock"
                                    className="w-3.5 h-3.5 invert-100 dark:invert-0"
                                  />
                                </button>
                              )}

                            {onOpenInsiderTrading &&
                              (record.insider_trading === "Buying" ||
                                record.insider_trading === "Selling") && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenInsiderTrading(record.symbol);
                                  }}
                                  title="Insider Trading"
                                  className="inline-flex shrink-0 items-center justify-center hover:opacity-80 transition-opacity ml-0.5"
                                >
                                  <Image
                                    src={SwapIcon}
                                    alt="Swap"
                                    className="w-4 h-4 dark:invert"
                                    style={{
                                      filter:
                                        record.insider_trading === "Buying"
                                          ? "brightness(0) saturate(100%) invert(31%) sepia(98%) saturate(2089%) hue-rotate(131deg) brightness(97%) contrast(100%)"
                                          : "brightness(0) saturate(100%) invert(27%) sepia(100%) saturate(7498%) hue-rotate(357deg) brightness(99%) contrast(116%)",
                                    }}
                                  />
                                </button>
                              )}
                          </div>
                        </div>

                        {/* Market Cap Subtitle */}
                        <div className="text-[11px] text-gray-500 dark:text-[#a0a5b8] ml-4">
                          MCap: {formatToCr(record.market_cap) || "—"}
                        </div>
                      </div>
                      {onOpenInsights && (
                        <div className="flex-shrink-0 ml-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenInsights(record.symbol, "overview");
                            }}
                            title="Company Info"
                            className="cursor-pointer inline-flex items-center justify-center hover:opacity-80 transition-opacity"
                          >
                            <Info className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  {/* Risk Profile */}
                  <td className="py-2 px-2 text-center font-figtree">
                    <span className="text-[13px] font-medium text-black dark:text-white">
                      {record.riskprofile || "-"}
                    </span>
                  </td>
                  {/* Close Price */}
                  <td className="text-center py-2 px-2 text-[14px] font-medium text-black dark:text-white font-figtree transition-colors duration-200">
                    {formatCurrencyWithLocalString(record.recommended_price)}
                  </td>

                  {/* 52 Week High (Stacked) */}
                  <td className="py-2 px-2 text-center font-figtree">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <span
                        className={`text-[13px] font-medium ${fiftyTwoWeekHighColor}`}
                      >
                        {formatPercentageWithLocalString(
                          record.fifty_two_week_high_perc,
                        )}
                      </span>
                      <span className="text-[12px] text-gray-700 dark:text-[#a0a5b8]">
                        {formatCurrencyWithLocalString(
                          record.fifty_two_week_high,
                        )}
                      </span>
                    </div>
                  </td>

                  {/* Probability */}
                  <td className="py-2 px-2 text-center font-figtree">
                    <span className="text-[13px] font-medium text-black dark:text-white">
                      {record.probability
                        ? formatPercentageWithLocalString(
                            record.probability * 100,
                          )
                        : "-"}
                    </span>
                  </td>

                  {/* RSI */}
                  <td className="py-2 px-0 font-figtree">
                    <div className="flex justify-around items-center min-w-[180px]">
                      {Object.entries({
                        daily: record.rsi_daily,
                        weekly: record.rsi_weekly,
                        monthly: record.rsi_monthly,
                      }).map(([key, score], idx) => {
                        let lightBg = "";
                        let darkBg = "";
                        let textColor = "text-black";

                        // Refined colors from the design
                        if (key === "monthly") {
                          lightBg = "bg-[#fcdfdf]";
                          darkBg = "dark:bg-[#4d2d32]";
                          textColor = "dark:text-[#ff8a8a]";
                        } else if (key === "weekly") {
                          lightBg = "bg-[#ffc79a]";
                          darkBg = "dark:bg-[#4a3623]";
                          textColor = "dark:text-[#ffb370]";
                        } else if (key === "daily") {
                          lightBg = "bg-[#d4fde5]";
                          darkBg = "dark:bg-[#203a2e]";
                          textColor = "dark:text-[#7cf4ad]";
                        } else {
                          lightBg = "bg-[#6B7280]";
                          darkBg = "dark:bg-[#4B5563]";
                          textColor = "dark:text-white";
                        }

                        return (
                          <div key={idx} className="flex justify-center w-1/3">
                            <span
                              className={`px-3 py-0.5 rounded-full text-[11px] font-medium font-figtree text-center ${lightBg} ${darkBg} ${textColor}`}
                            >
                              {score?.toFixed(2) || "-"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </td>

                  {/* Peak Return (Stacked) */}
                  <td className="py-2 px-2 text-center font-figtree">
                    <div className="flex flex-col items-center justify-center gap-0.5 group">
                      <span
                        className={`text-[13px] font-medium ${peakReturnColor}`}
                      >
                        {Number(record.high_return_perc) !== 0.0
                          ? `${formatPercentageWithLocalString(record.high_return_perc)}`
                          : "-"}
                      </span>
                      <div className="flex items-center gap-1">
                        <FlashingValue
                          value={
                            (record.highest_price_afterdate ||
                              record.todays_high) > 0
                              ? formatCurrencyWithLocalString(
                                  record.highest_price_afterdate ||
                                    record.todays_high,
                                )
                              : "-"
                          }
                          isPositive={
                            Number(record.high_return_perc) === 0
                              ? null
                              : Number(record.high_return_perc) > 0
                          }
                          className="text-[12px]"
                        />
                        {/* Trend icon if high return > 0 */}
                        {Number(record.high_return_perc) > 0 && onOpenChart && (
                          <TrendingUp
                            className="w-4 h-4 text-green-500 dark:text-[#00a85f] opacity-80 hover:opacity-100 cursor-pointer transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOpenChart(
                                record.symbol,
                                moment(record.date)
                                  .tz("Asia/Kolkata")
                                  .format("YYYY-MM-DD"),
                              );
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Current Return (Stacked) */}
                  <td className="py-2 px-2 text-center font-figtree">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <span
                        className={`text-[13px] font-medium ${currentReturnColor}`}
                      >
                        {formatPercentageWithLocalString(
                          record.close && record.ltp
                            ? (
                                ((record.ltp - record.close) / record.close) *
                                100
                              ).toFixed(2)
                            : "0.00",
                        )}
                      </span>
                      <FlashingValue
                        value={formatCurrencyWithLocalString(record.ltp)}
                        isPositive={
                          record.ltp &&
                          record.close &&
                          record.ltp - record.close === 0
                            ? null
                            : !(
                                record.ltp &&
                                record.close &&
                                record.ltp - record.close < 0
                              )
                        }
                        flashTrigger={record.lastUpdated}
                        className="text-[12px] font-medium"
                      />
                    </div>
                  </td>

                  {/* Score Bars */}
                  <td className="py-2 px-1 align-middle">
                    {renderScoreBar(
                      record.financial_score,
                      false,
                      () =>
                        onOpenInsights &&
                        onOpenInsights(record.symbol, "fundamentals"),
                      isSelected,
                    )}
                  </td>
                  <td className="py-2 px-1 align-middle">
                    {renderScoreBar(
                      record.fundamental_score,
                      false,
                      () =>
                        onOpenBusinessInsight &&
                        onOpenBusinessInsight(record.symbol),
                      isSelected,
                    )}
                  </td>
                  <td className="py-2 px-1 align-middle">
                    {renderScoreBar(
                      record.forensic_score,
                      false,
                      () =>
                        onOpenForensicInsight &&
                        onOpenForensicInsight(record.symbol),
                      isSelected,
                    )}
                  </td>
                  <td
                    className="py-2 px-1 align-middle cursor-default"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {renderScoreBar(
                      record.overall_score,
                      true,
                      undefined,
                      isSelected,
                    )}
                  </td>

                  {/* Actions */}
                  <td className="py-2 px-3 text-center">
                    <div className="flex items-center justify-center space-x-3">
                      {/* Trash or Briefcase */}
                      {onRemoveWatchlist ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemoveWatchlist(record);
                          }}
                          title="Remove from Watchlist"
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-[18px] h-[18px]" />
                        </button>
                      ) : onAddPortfolio ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              isPortfolioLimitReached &&
                              isPortfolioLimitReached(record) &&
                              !record?.added_to_portfolio
                            ) {
                              if (onLimitReached) onLimitReached("portfolio");
                              return;
                            }
                            onAddPortfolio(record);
                          }}
                          title={
                            isPortfolioLimitReached &&
                            isPortfolioLimitReached(record) &&
                            !record?.added_to_portfolio
                              ? "Free trial limit reached"
                              : "Add to Portfolio"
                          }
                          className={`transition-all duration-200 ${
                            isPortfolioLimitReached &&
                            isPortfolioLimitReached(record) &&
                            !record?.added_to_portfolio
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:opacity-80"
                          }`}
                        >
                          <Image
                            src={
                              record?.added_to_portfolio
                                ? BriefCaseFilledIcon
                                : Briefcase
                            }
                            alt="Briefcase"
                            style={{
                              height: "auto",
                              filter: record?.added_to_portfolio
                                ? "invert(62%) sepia(66%) saturate(1200%) hue-rotate(350deg) brightness(100%) contrast(95%)"
                                : "",
                            }}
                            className={`w-[18px] h-[18px] transition-all duration-200 ${
                              record?.added_to_portfolio
                                ? "filter-orange"
                                : "filter invert dark:invert-0 dark:brightness-100"
                            }`}
                          />
                        </button>
                      ) : null}

                      {/* Watchlist */}
                      {onAddWatchlist && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              isWatchlistLimitReached &&
                              isWatchlistLimitReached(record)
                            ) {
                              if (onLimitReached) onLimitReached("watchlist");
                              return;
                            }
                            onAddWatchlist(record);
                          }}
                          title={
                            isWatchlistLimitReached &&
                            isWatchlistLimitReached(record)
                              ? "Free trial limit reached"
                              : "Add to Watchlist"
                          }
                          className={`transition-all duration-200 ${
                            isWatchlistLimitReached &&
                            isWatchlistLimitReached(record)
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:opacity-80"
                          }`}
                        >
                          <Image
                            src={
                              record?.added_to_watchlist
                                ? WatchlistFilledIcon
                                : WatchlistIcon
                            }
                            alt="Watchlist"
                            style={{
                              height: "auto",
                              filter: record?.added_to_watchlist
                                ? "invert(62%) sepia(66%) saturate(1200%) hue-rotate(350deg) brightness(100%) contrast(95%)"
                                : "",
                            }}
                            className={`w-[18px] h-[18px] transition-all duration-200 ${
                              record?.added_to_watchlist
                                ? "filter-orange"
                                : "filter invert dark:invert-0 dark:brightness-100"
                            }`}
                          />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CommonStockTable;
